import {
  createSlice,
  createAsyncThunk,
  miniSerializeError,
  SerializedError,
} from "@reduxjs/toolkit";
import authService, { UserData } from "../services/authService";
import simulationService from "../services/simulationService";
import { userService } from "../services/userService";
import { LOADING_STATE } from "../types/constants";
import { hasLength } from "../utils/dataTransformations";
import {
  ldapToRoleFlags,
  realLdapToSimulatableLdap,
  realLdapToSimulatedLdap,
  simulatedLdapToEffectiveLdap,
  storedToLdapGroups,
} from "../utils/ldapGroups";

/**
 * User state interface for Redux store
 */
export interface UserState {
  // User information
  token: string;
  alias: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];  // effective groups: the simulated groups while simulating, otherwise the real ones
  siteCode: string;

  // Groups an admin is currently simulating; empty when not simulating
  simulatedLdap: string[];
  // Groups the real (not simulated) user may simulate; empty for non-admins
  simulatableLdap: string[];

  // Sites the user belongs to (admin-managed membership from GET /api/usermetadata).
  // Distinct from siteCode (their badge location from the JWT).
  sites: string[];
  // Load status of the GET /api/usermetadata fetch (populates sites + pluginVersion).
  metadataStatus: LOADING_STATE;

  // Autofill userscript version last reported to the backend (GET /api/usermetadata).
  // '' = never reported or uninstalled. Used to diff against the locally-detected version.
  pluginVersion: string;

  // Computed permission flags (set on login)
  isEngineer: boolean;  // workingwell-engineer — highest privilege (delete, profile CRUD)
  isAdmin: boolean;     // workingwell-engineer OR whs-data-navigator-admin — elevated access (view all sessions)
  isAuditor: boolean;   // whs-data-navigator-auditor — may audit validated sessions; read-only
                        // on sessions that are neither theirs nor at one of their sites

  // Status information
  userLoading: LOADING_STATE;
  authError: SerializedError | null;
  hasPermission: boolean;
  pageLastLoadTimestamp: number;
}

/**
 * Initial user state
 */
const initialState: UserState = {
  token: "",
  alias: "",
  lastName: "",
  firstName: "",
  email: "",
  ldap: [],
  siteCode: "",
  simulatedLdap: [],
  simulatableLdap: [],
  sites: [],
  metadataStatus: "idle",
  pluginVersion: "",
  isEngineer: false,
  isAdmin: false,
  isAuditor: false,
  userLoading: "idle",
  authError: null,
  hasPermission: true,
  pageLastLoadTimestamp: 0,
};

/**
 * Async thunk to get the authenticated user
 */
export const getUser = createAsyncThunk<UserData & { simulatedLdap: string[] }>("user/getUser", async () => {
  const user = await authService.login();
  const stored = simulationService.storedSimulation();
  const simulatedLdap = realLdapToSimulatedLdap(user.ldap)(storedToLdapGroups(stored));

  // A stale or disallowed simulation would get every request rejected
  if (stored !== null && !hasLength(simulatedLdap)) {
    simulationService.clearSimulation();
  }

  return { ...user, simulatedLdap };
});

/**
 * Async thunk to load the user's site memberships from the backend.
 */
export const fetchUserMetadata = createAsyncThunk("user/fetchUserMetadata", async () => {
  return userService.fetchUserMetadata();
});

/** Report the locally-detected autofill userscript version to the backend. */
export const reportPluginVersion = createAsyncThunk(
  "user/reportPluginVersion",
  (version: string) => userService.updatePluginVersion(version),
);

/**
 * User slice definition
 */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    /**
     * Manual logout action
     */
    logoutUser: (state) => {
      // Reset user state to initial values
      state.token = "";
      state.alias = "";
      state.lastName = "";
      state.firstName = "";
      state.email = "";
      state.ldap = [];
      state.simulatedLdap = [];
      state.simulatableLdap = [];
      state.userLoading = "idle";
      state.authError = null;
      
      // Call logout function from auth service
      authService.logout();
    },
    
    /**
     * Clear auth error
     */
    clearAuthError: (state) => {
      state.authError = null;
    },
  },
  extraReducers: (builder) => {
    /**
     * Handle pending state when getting user
     */
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = "pending";
    });
    
    /**
     * Handle successful user authentication
     */
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, ldap, siteCode, simulatedLdap } = payload;

      // Set user information
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.ldap = simulatedLdapToEffectiveLdap(simulatedLdap)(ldap);
      state.siteCode = siteCode;
      state.simulatedLdap = simulatedLdap;
      state.simulatableLdap = realLdapToSimulatableLdap(ldap);
      
      // Set status information
      state.userLoading = "fulfilled";
      state.pageLastLoadTimestamp = Date.now();
      
      // Compute role flags (any recognized group grants app access)
      const { isEngineer, isAdmin, isAuditor, hasPermission } = ldapToRoleFlags(state.ldap);
      state.isEngineer = isEngineer;
      state.isAdmin = isAdmin;
      state.isAuditor = isAuditor;
      state.hasPermission = hasPermission;
    });
    
    /**
     * Handle authentication failure
     */
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.authError = miniSerializeError(error);
      state.userLoading = "rejected";
      state.hasPermission = false;
    });

    // User metadata (sites + autofill plugin version)
    builder.addCase(fetchUserMetadata.pending, (state) => {
      state.metadataStatus = "pending";
    });
    builder.addCase(fetchUserMetadata.fulfilled, (state, { payload }) => {
      state.metadataStatus = "fulfilled";
      state.sites = payload.sites;
      state.pluginVersion = payload.autofill_plugin_version ?? "";
    });
    builder.addCase(fetchUserMetadata.rejected, (state) => {
      state.metadataStatus = "rejected";
    });
  },
});

// Export actions
export const { logoutUser, clearAuthError } = userSlice.actions;

// Export reducer
export default userSlice.reducer;
